import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import SeatMapView from "@/components/site/SeatMapView";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

/**
 * AdminAddonPanel — post-ticketing ancillary servicing UI.
 *
 * Loads LIVE seat/bag/meal options from the supplier (Amadeus seatmaps +
 * pricing-with-bags, Travelport AirMerchandisingDetails + SeatMap) and lets
 * the admin pick exactly what the customer would have picked on the B2C
 * funnel, with the carrier's real price. On submit, the chosen items are
 * recorded via `POST /admin/bookings/{id}/add-addon` so they appear on the
 * customer's voucher and the supplier-side PNR can be SSR-updated.
 *
 * Supplier branching:
 *   - Amadeus  → uses booking.item.segments[].amadeus_offer  (`/flights/seatmap`, `/flights/ancillaries`)
 *   - Travelport → uses the flight built from booking.item.segments[].travelport_segments (`/travelport/seatmap`, `/travelport/ancillaries`)
 *   - No supplier offer payload → renders a manual "log only" fallback form so
 *     legacy bookings can still be serviced.
 */

const SSR_MEALS = [
  { id: "VGML", name: "Vegetarian (VGML)", price: 0 },
  { id: "AVML", name: "Asian Vegetarian (AVML)", price: 0 },
  { id: "HNML", name: "Hindu Meal (HNML)", price: 0 },
  { id: "MOML", name: "Muslim / Halal (MOML)", price: 0 },
  { id: "KSML", name: "Kosher (KSML)", price: 0 },
  { id: "GFML", name: "Gluten-Free (GFML)", price: 0 },
  { id: "DBML", name: "Diabetic (DBML)", price: 0 },
  { id: "CHML", name: "Child Meal (CHML)", price: 0 },
  { id: "BBML", name: "Baby Meal (BBML)", price: 0 },
];

function buildTravelportFlightFromBooking(booking, segIdx) {
  const seg = (booking.item?.segments || [])[segIdx] || {};
  return {
    travelport_segments: seg.travelport_segments || [],
    plating_carrier: seg.plating_carrier || seg.airline_code,
    fare_basis: seg.fare_basis,
    rbd: seg.rbd,
  };
}

export default function AdminAddonPanel({ booking, onSaved }) {
  const supplier = (booking.supplier_source || booking.supplier || "").toString().toLowerCase();
  const segments = booking.item?.segments || [];
  const travelers = booking.travelers || [];

  const [kind, setKind] = useState("seat");
  const [segIdx, setSegIdx] = useState(0);
  const [paxIdx, setPaxIdx] = useState(0);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Live supplier payloads
  const [ancillaries, setAncillaries] = useState(null);
  const [ancLoading, setAncLoading] = useState(false);
  const [ancError, setAncError] = useState("");

  // Selections
  const [seatPick, setSeatPick] = useState(null); // {number, price}
  const [bagPick, setBagPick] = useState(null);
  const [mealPick, setMealPick] = useState(null);

  const currentSeg = segments[segIdx] || {};
  const hasAmadeusOffer = !!currentSeg.amadeus_offer;
  const hasTravelportSegs = (currentSeg.travelport_segments || []).length > 0;
  const supplierAvailable = (supplier === "amadeus" && hasAmadeusOffer) || (supplier === "travelport" && hasTravelportSegs);

  // ── Fetch ancillaries (bags + meals) when seg or kind changes ────────────────
  useEffect(() => {
    if (kind === "seat") return;          // seats use SeatMapView directly
    if (!supplierAvailable) return;
    let alive = true;
    setAncLoading(true); setAncError("");
    (async () => {
      try {
        const url = supplier === "travelport" ? "/travelport/ancillaries" : "/flights/ancillaries";
        const body = supplier === "travelport"
          ? { flight: buildTravelportFlightFromBooking(booking, segIdx) }
          : { flight_offer: currentSeg.amadeus_offer };
        const { data } = await api.post(url, body);
        if (!alive) return;
        setAncillaries(data || {});
      } catch (e) {
        if (alive) setAncError(e?.response?.data?.detail || e?.message || "Ancillary lookup failed");
      } finally {
        if (alive) setAncLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [kind, segIdx, supplier, supplierAvailable]);  // eslint-disable-line

  const bagItems  = useMemo(() => ancillaries?.bags?.options || [], [ancillaries]);
  const paidMeals = useMemo(() => ancillaries?.meals?.options || [], [ancillaries]);
  const mealOptions = useMemo(() => [...paidMeals, ...SSR_MEALS], [paidMeals]);

  const submit = async () => {
    let pick = null;
    if (kind === "seat") pick = seatPick;
    if (kind === "bag")  pick = bagPick;
    if (kind === "meal") pick = mealPick;
    if (!pick) {
      toast.error(`Please select a ${kind}`);
      return;
    }
    const label = kind === "seat"
      ? `Seat ${pick.number || pick.seat_number}`
      : (pick.name || pick.id || kind);
    const price = Number(pick.price || pick.totalPrice || 0);
    try {
      setSubmitting(true);
      await api.post(`/admin/bookings/${booking.id}/add-addon`, {
        items: [{
          kind,
          segment_index: Number(segIdx),
          pax_index: Number(paxIdx),
          label,
          price,
          supplier,
          sub_code: pick.id || pick.sub_code || "",
        }],
        note,
      });
      toast.success(`${kind === "seat" ? "Seat" : kind === "bag" ? "Bag" : "Meal"} added: ${label}${price > 0 ? ` · $${price.toFixed(2)}` : ""}`);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally     { setSubmitting(false); }
  };

  return (
    <div className="space-y-4">
      {/* Live-supplier banner */}
      <div className={`text-xs px-3 py-2 rounded-lg ${supplierAvailable ? "bg-teal-50 text-teal-800" : "bg-amber-50 text-amber-800"}`}>
        {supplierAvailable
          ? <>Connected to <strong className="capitalize">{supplier}</strong> — prices & seat map are live from the carrier.</>
          : <>No supplier offer payload on this booking — falling back to manual logging only. New bookings will have live add-ons available.</>}
      </div>

      {/* Type + Segment + Pax */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label>Type</Label>
          <Select value={kind} onValueChange={(v) => { setKind(v); setSeatPick(null); setBagPick(null); setMealPick(null); }}>
            <SelectTrigger data-testid="admin-addon-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="seat">Seat</SelectItem>
              <SelectItem value="bag">Bag</SelectItem>
              <SelectItem value="meal">Meal</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Segment</Label>
          <Select value={String(segIdx)} onValueChange={(v) => setSegIdx(Number(v))}>
            <SelectTrigger data-testid="admin-addon-segment"><SelectValue /></SelectTrigger>
            <SelectContent>
              {segments.map((s, i) => (
                <SelectItem key={i} value={String(i)}>
                  {s.airline_code || ""}{s.flight_number || ""} · {s.from || s.origin}→{s.to || s.destination} · {s.date}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Passenger</Label>
          <Select value={String(paxIdx)} onValueChange={(v) => setPaxIdx(Number(v))}>
            <SelectTrigger data-testid="admin-addon-pax"><SelectValue /></SelectTrigger>
            <SelectContent>
              {travelers.map((t, i) => (
                <SelectItem key={i} value={String(i)}>
                  {`${i + 1}. ${[t.title, t.first_name, t.last_name].filter(Boolean).join(" ") || t.name || "Pax"}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Seat — Seat map UI */}
      {kind === "seat" && (
        <div className="border border-slate-200 rounded-xl p-3">
          {supplierAvailable ? (
            <SeatMapView
              supplier={supplier === "travelport" ? "travelport" : "amadeus"}
              flightOffer={currentSeg.amadeus_offer}
              travelportFlight={buildTravelportFlightFromBooking(booking, segIdx)}
              segmentIndex={segIdx}
              selected={seatPick?.number || null}
              onSelect={(seatNumber, priceUSD) => setSeatPick({ number: seatNumber, price: Number(priceUSD || 0) })}
            />
          ) : (
            <div className="text-sm text-slate-500 italic">Seat map unavailable. Type a seat number manually below.</div>
          )}
          <div className="mt-3 flex items-center gap-2">
            <Label className="text-xs">Or enter seat manually</Label>
            <input
              type="text" placeholder="12A" maxLength={4}
              data-testid="admin-addon-seat-manual"
              className="border border-slate-200 rounded-md px-2 py-1 text-sm w-24 uppercase"
              value={seatPick?.number || ""}
              onChange={(e) => setSeatPick({ number: e.target.value.toUpperCase(), price: 0 })}
            />
            {seatPick?.price > 0 && (
              <span className="ml-auto text-xs text-teal-700 font-medium">+${seatPick.price.toFixed(2)}</span>
            )}
          </div>
        </div>
      )}

      {/* Bag — live priced dropdown */}
      {kind === "bag" && (
        <div className="border border-slate-200 rounded-xl p-3">
          {ancLoading
            ? <div className="text-xs text-slate-500 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" /> Loading live bag options…</div>
            : ancError
              ? <div className="text-xs text-rose-600">{ancError}</div>
              : bagItems.length === 0
                ? <div className="text-xs text-slate-500 italic">Carrier did not publish bag options for this fare class.</div>
                : (
                  <Select value={bagPick?.id || ""} onValueChange={(v) => setBagPick(bagItems.find((b) => String(b.id) === v))}>
                    <SelectTrigger data-testid="admin-addon-bag"><SelectValue placeholder="Choose an extra bag" /></SelectTrigger>
                    <SelectContent>
                      {bagItems.map((b) => (
                        <SelectItem key={b.id} value={String(b.id)}>
                          {b.name || `${b.weight_kg || ""}kg × ${b.quantity || 1}`} · +${Number(b.price).toFixed(2)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
        </div>
      )}

      {/* Meal — paid + free SSR */}
      {kind === "meal" && (
        <div className="border border-slate-200 rounded-xl p-3">
          {ancLoading
            ? <div className="text-xs text-slate-500 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" /> Loading live meal options…</div>
            : ancError
              ? <div className="text-xs text-rose-600">{ancError}</div>
              : (
                <Select value={mealPick?.id || ""} onValueChange={(v) => setMealPick(mealOptions.find((m) => String(m.id) === v))}>
                  <SelectTrigger data-testid="admin-addon-meal"><SelectValue placeholder="Choose a meal preference" /></SelectTrigger>
                  <SelectContent>
                    {paidMeals.map((m) => (
                      <SelectItem key={`paid-${m.id}`} value={String(m.id)}>{m.name} · +${Number(m.price).toFixed(2)}</SelectItem>
                    ))}
                    {SSR_MEALS.map((m) => (
                      <SelectItem key={`ssr-${m.id}`} value={String(m.id)}>{m.name} · Free (SSR)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
        </div>
      )}

      <div>
        <Label>Internal note (optional)</Label>
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. customer requested via WhatsApp" />
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
        <Button variant="ghost" onClick={() => onSaved?.()}>Cancel</Button>
        <Button
          data-testid="admin-bk-addon-save"
          disabled={submitting}
          onClick={submit}
          className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full"
        >
          {submitting ? "Saving…" : "Service add-on"}
        </Button>
      </div>
    </div>
  );
}
